import { getLocalItem, getSessionItem } from '../utils/safeStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ADMIN_ACCESS_KEY = 'adminAccessToken';
const LEGACY_ADMIN_ACCESS_KEY = 'hv_admin_token';

const resolveAdminToken = (token) =>
  token ||
  getSessionItem(ADMIN_ACCESS_KEY) ||
  getLocalItem(ADMIN_ACCESS_KEY) ||
  getSessionItem(LEGACY_ADMIN_ACCESS_KEY) ||
  getLocalItem(LEGACY_ADMIN_ACCESS_KEY) ||
  '';

const authHeaders = (token, extra = {}) => {
  const effectiveToken = resolveAdminToken(token);
  return effectiveToken
    ? { Authorization: `Bearer ${effectiveToken}`, ...extra }
    : extra;
};

const handle = async (res) => {
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data?.message || 'Request failed');
  return data;
};

export const adminListHosts = async (token, { page = 1, limit = 20, status, q, kyc_status, payout_status } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (status) params.set('status', status);
  if (kyc_status) params.set('kyc_status', kyc_status);
  if (payout_status) params.set('payout_status', payout_status);
  if (q) params.set('q', q);
  const res = await fetch(`${API_BASE}/api/admin/hosts?${params.toString()}`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminGetHost = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminHostDashboard = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/dashboard`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminSetHostStatus = async (token, id, status, body = {}) => {
  const map = {
    APPROVED: 'approve',
    REJECTED: 'reject',
    BLOCKED: 'block',
    UNBLOCK: 'unblock',
  };
  const action = map[status] || 'approve';
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/${action}`, {
    method: 'PATCH',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminHostDocuments = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/documents`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminVerifyDocument = async (token, docId, body = {}) => {
  return adminUpdateKycDoc(token, docId, { status: 'APPROVED', admin_comment: body.review_notes || body.admin_comment });
};

export const adminRejectDocument = async (token, docId, body = {}) => {
  return adminUpdateKycDoc(token, docId, { status: 'REJECTED', admin_comment: body.review_notes || body.admin_comment });
};

export const adminUpdateKycDoc = async (token, docId, body = {}) => {
  const res = await fetch(`${API_BASE}/api/admin/kyc/${docId}`, {
    method: 'PATCH',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminHostProperties = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/properties`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminCreateHostProperty = async (token, id, body) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/properties`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminHostBookings = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/bookings`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminHostPerformance = async (token, id, range = 30) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/performance?range=${range}`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminHostPayouts = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/payouts`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminApproveKyc = async (token, id) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/kyc/approve`, {
    method: 'POST',
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminRejectKyc = async (token, id, body = {}) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/kyc/reject`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminCreatePayout = async (token, id, body) => {
  const res = await fetch(`${API_BASE}/api/admin/hosts/${id}/payouts`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminPayoutAction = async (token, payoutId, action, body = {}) => {
  const urlMap = {
    APPROVE: 'approve',
    DISAPPROVE: 'disapprove',
    PAID: 'mark-paid',
  };
  const actionPath = urlMap[action] || 'approve';
  const res = await fetch(`${API_BASE}/api/admin/payouts/${payoutId}/${actionPath}`, {
    method: 'PATCH',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return handle(res);
};

export const adminListHostBankAccounts = async (token, { status, q, page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  if (page) params.set('page', page);
  if (limit) params.set('limit', limit);
  const res = await fetch(`${API_BASE}/api/admin/finance/bank-accounts?${params.toString()}`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminHostBankAccount = async (token, hostId) => {
  const res = await fetch(`${API_BASE}/api/admin/finance/hosts/${hostId}/bank-account`, {
    headers: authHeaders(token),
    credentials: 'include',
  });
  return handle(res);
};

export const adminUpdateHostBankAccountStatus = async (token, hostId, status, rejectionReason) => {
  const payload = { status };
  if (rejectionReason) payload.rejectionReason = rejectionReason;
  const res = await fetch(`${API_BASE}/api/admin/finance/hosts/${hostId}/bank-account/status`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handle(res);
};


